import type { FilterProperty, PropertyFilterQuery } from "@/components/table";
import { queryToFilterParams } from "@/components/table";
import { DELAY_SOURCE_OPTIONS } from "@/features/bookings/report/bookingReportOptions";
import { BOOKING_TAG_FILTER_OPTIONS } from "@/lib/bookingTagFilterOptions";
import { loadCarrierFilterOptions } from "@/lib/carrierFilterOptions";
import { formatDimensionValue } from "@/lib/formatDimensionValue";
import { sortServiceTradeSuggestions } from "@/lib/serviceTradeConstants";
import { liveViewerScope } from "@/lib/viewerScopeFilters";
import { api } from "@/services/api";
import type { BookingsFilters } from "@/types/api";
import {
  BOOKING_STATE_LABELS,
  expandPendingStateParam,
  type BookingState,
} from "@/types/booking";

const BOOKING_DATE_PARAM_MAP = {
  etd: {
    ">=": "departure_from",
    "<=": "departure_to",
    ">": "departure_from",
    "<": "departure_to",
  },
  eta: {
    ">=": "eta_from",
    "<=": "eta_to",
    ">": "eta_from",
    "<": "eta_to",
  },
  pcd: {
    ">=": "pcd_from",
    "<=": "pcd_to",
    ">": "pcd_from",
    "<": "pcd_to",
  },
  cutoff: {
    ">=": "cutoff_from",
    "<=": "cutoff_to",
    ">": "cutoff_from",
    "<": "cutoff_to",
  },
  stuffing: {
    ">=": "stuffing_from",
    "<=": "stuffing_to",
    ">": "stuffing_from",
    "<": "stuffing_to",
  },
  ready_date: {
    ">=": "ready_date_from",
    "<=": "ready_date_to",
    ">": "ready_date_from",
    "<": "ready_date_to",
  },
  open_date: {
    ">=": "open_date_from",
    "<=": "open_date_to",
    ">": "open_date_from",
    "<": "open_date_to",
  },
  last_update: {
    ">=": "last_updated_from",
    "<=": "last_updated_to",
    ">": "last_updated_from",
    "<": "last_updated_to",
  },
  operational_flow_date: {
    ">=": "operational_flow_from",
    "<=": "operational_flow_to",
    ">": "operational_flow_from",
    "<": "operational_flow_to",
  },
} as const;

const STATE_OPTIONS = (
  Object.entries(BOOKING_STATE_LABELS) as [BookingState, string][]
).map(([value, label]) => ({ value, label }));

function loadFilterValues(property: string) {
  return async (query: string) => {
    const scope = liveViewerScope();
    const rows = await api.getBookingFilterValues(
      property,
      query.trim() || undefined,
      20,
      scope.linkedScope ? { linkedScope: true } : undefined,
    );
    const mapped = rows.map((row) => ({
      value: row.value,
      label: formatDimensionValue(property, row.label ?? row.value),
    }));
    if (property === "service_trade") {
      return sortServiceTradeSuggestions(mapped);
    }
    return mapped;
  };
}

/** Property order: status → lane → commercial → parties → dates → rebook → tags/equipment → delays/opportunity. */
export const BOOKING_FILTER_PROPERTIES: FilterProperty[] = [
  {
    key: "state",
    label: "State",
    kind: "enum",
    operators: [":", "!:", "=", "!="],
    defaultOperator: ":",
    options: STATE_OPTIONS,
  },
  {
    key: "carrier",
    label: "Carrier",
    kind: "text",
    loadOptions: async (query) => {
      const fromBookings = await loadFilterValues("carrier")(query).catch(
        () => [],
      );
      return loadCarrierFilterOptions(query, fromBookings);
    },
  },
  {
    key: "agent",
    label: "Agent",
    kind: "text",
    loadOptions: loadFilterValues("agent"),
  },
  {
    key: "origin_port",
    label: "Origin",
    kind: "text",
    operators: [":", "!:"],
    loadOptions: loadFilterValues("origin_port"),
  },
  {
    key: "destination_port",
    label: "Destination",
    kind: "text",
    operators: [":", "!:"],
    loadOptions: loadFilterValues("destination_port"),
  },
  {
    key: "service_trade",
    label: "Service trade",
    kind: "text",
    operators: [":", "!:"],
    loadOptions: loadFilterValues("service_trade"),
  },
  {
    key: "week",
    label: "Week",
    kind: "text",
    operators: ["="],
    defaultOperator: "=",
    loadOptions: loadFilterValues("week"),
  },
  {
    key: "contract_ref",
    label: "Contract",
    kind: "text",
    operators: [":", "!:"],
    loadOptions: loadFilterValues("contract_ref"),
  },
  {
    key: "shipper",
    label: "Shipper",
    kind: "text",
    operators: [":", "!:"],
    loadOptions: loadFilterValues("shipper"),
  },
  {
    key: "consignee",
    label: "Consignee",
    kind: "text",
    operators: [":", "!:"],
    loadOptions: loadFilterValues("consignee"),
  },
  {
    key: "sales_person",
    label: "Sales",
    kind: "text",
    loadOptions: loadFilterValues("sales_person"),
  },
  {
    key: "opened_by",
    label: "Opened by",
    kind: "text",
    operators: [":", "!:", "=", "!="],
    defaultOperator: ":",
    loadOptions: loadFilterValues("opened_by"),
  },
  { key: "open_date", label: "Open date", kind: "date" },
  { key: "ready_date", label: "Ready date", kind: "date" },
  { key: "stuffing", label: "Stuffing", kind: "date" },
  { key: "cutoff", label: "Cutoff", kind: "date" },
  { key: "etd", label: "ETD", kind: "date" },
  { key: "eta", label: "ETA", kind: "date" },
  { key: "pcd", label: "PCD", kind: "date" },
  { key: "last_update", label: "Last update", kind: "date" },
  {
    key: "rebook_from_contract",
    label: "Rebook from contract",
    kind: "text",
    operators: [":", "!:"],
    loadOptions: loadFilterValues("rebook_from_contract"),
  },
  {
    key: "rebook_to_contract",
    label: "Rebook to contract",
    kind: "text",
    operators: [":", "!:"],
    loadOptions: loadFilterValues("rebook_to_contract"),
  },
  {
    key: "tag",
    label: "Tag",
    kind: "enum",
    options: BOOKING_TAG_FILTER_OPTIONS,
  },
  {
    key: "load_type",
    label: "Load type",
    kind: "enum",
    options: [
      { value: "fcl", label: "FCL" },
      { value: "lcl", label: "LCL" },
    ],
  },
  {
    key: "equipment_size",
    label: "Equipment size",
    kind: "enum",
    options: [
      { value: "20", label: "20'" },
      { value: "40", label: "40'" },
      { value: "45", label: "45'" },
    ],
  },
  {
    key: "equipment_type",
    label: "Equipment type",
    kind: "enum",
    options: [
      { value: "DV", label: "DV (Dry Van)" },
      { value: "HC", label: "HC (High Cube)" },
      { value: "Reefer", label: "Reefer" },
      { value: "OT", label: "OT (Open Top)" },
      { value: "FR", label: "FR (Flat Rack)" },
      { value: "TK", label: "TK (Tank)" },
      { value: "DG", label: "DG (Dangerous Goods)" },
      { value: "OOG", label: "OOG (Out of Gauge)" },
    ],
  },
  {
    key: "weight",
    label: "Weight (kg)",
    kind: "number",
  },
  {
    key: "total_containers",
    label: "Ctrs",
    kind: "number",
    min: 0,
    integer: true,
  },
  {
    key: "delay_days",
    label: "ETD delay (days)",
    kind: "number",
  },
  {
    key: "delay_source",
    label: "ETD delay source",
    kind: "enum",
    options: DELAY_SOURCE_OPTIONS,
  },
  {
    key: "eta_delay_days",
    label: "ETA delay (days)",
    kind: "number",
  },
  {
    key: "eta_delay_source",
    label: "ETA delay source",
    kind: "enum",
    options: DELAY_SOURCE_OPTIONS,
  },
  {
    key: "has_opportunity",
    label: "Opportunity",
    kind: "enum",
    options: [
      { value: "yes", label: "Has opportunity" },
      { value: "no", label: "No opportunity" },
    ],
  },
  {
    key: "opportunity_kind",
    label: "Opportunity kind",
    kind: "enum",
    options: [
      { value: "rebook", label: "Rebook opportunity" },
      { value: "first_book", label: "First book (no contract)" },
      { value: "consolidation", label: "LCL consolidation" },
    ],
  },
];

const BOOKING_NUMBER_PARAM_MAP = {
  weight: {
    ">=": "weight_gte",
    "<=": "weight_lte",
    "=": "weight_eq",
  },
  delay_days: {
    ">=": "delay_days_gte",
    "<=": "delay_days_lte",
    "=": "delay_days_eq",
  },
  eta_delay_days: {
    ">=": "eta_delay_days_gte",
    "<=": "eta_delay_days_lte",
    "=": "eta_delay_days_eq",
  },
  total_containers: {
    ">=": "total_containers_gte",
    "<=": "total_containers_lte",
    "=": "total_containers_eq",
  },
} as const;

export function queryToBookingsFilters(
  query: PropertyFilterQuery,
): Omit<BookingsFilters, "page" | "per_page"> {
  const filters = queryToFilterParams(query, {
    dates: BOOKING_DATE_PARAM_MAP,
    numbers: BOOKING_NUMBER_PARAM_MAP,
  });
  const state = expandPendingStateParam(filters.state);
  return state ? { ...filters, state } : filters;
}

export function mergeBookingsFilters(
  query: PropertyFilterQuery,
  extra: BookingsFilters = {},
): BookingsFilters {
  const fromQuery = queryToBookingsFilters(query);
  return {
    ...fromQuery,
    ...extra,
    state: expandPendingStateParam(fromQuery.state ?? extra.state),
    search: [fromQuery.search, extra.search].filter(Boolean).join(" ") || undefined,
  };
}
